import React from "react";
import { RotateCcw, Check } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";

export const JobPostRemoveInterestDialog: React.FC<{
  jobId: string;
  onClose: () => void;
  onSuccess: () => void;
  previousDecision?: boolean;
}> = ({ jobId, onClose, onSuccess, previousDecision }) => {
  const trpc = useTRPC();

  const {
    mutateAsync: setDecision,
    isError,
    isPending,
  } = useMutation(
    trpc.jobPost.setDecision.mutationOptions({
      onSuccess: () => {
        onSuccess();
        toast(
          previousDecision === true
            ? "Application Withdrawn"
            : "Decision Removed",
          {
            description:
              previousDecision === true
                ? "Your application for this position has been withdrawn."
                : "Your previous decision has been removed.",
            duration: 3000,
            icon: <Check className="h-5 w-5 text-green-500" />,
          },
        );
        onClose();
      },
    }),
  );

  const handleRemoveInterest = async (): Promise<void> => {
    await setDecision({
      id: jobId,
      interested: null,
      decisionReasonIds: [],
    });
  };

  const handleCancel = (): void => {
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="[&.dialog-content]:max-w-[min(32rem,calc(100vw-2rem))] [&.dialog-content]:max-h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)] w-full overflow-y-auto rounded-2xl border-slate-200 bg-white text-slate-900 sm:max-w-lg sm:rounded-2xl">
        <DialogHeader>
          <div className="mx-auto bg-amber-50 p-4 rounded-2xl mb-4 w-16 h-16 flex items-center justify-center">
            <RotateCcw className="h-6 w-6 text-amber-600" />
          </div>
          <DialogTitle className="text-center text-2xl font-semibold tracking-tight text-slate-900">
            {previousDecision === true
              ? "Withdraw Application?"
              : "Remove Decision?"}
          </DialogTitle>
          <DialogDescription className="text-center text-sm leading-relaxed text-slate-600 sm:px-4">
            {previousDecision === true
              ? "This will withdraw your application. Your profile will no longer be shared with the facility for this job posting."
              : "This will remove your previous decision for this position. You'll be able to make a new decision about this job."}
          </DialogDescription>
        </DialogHeader>

        {isError && (
          <Alert variant="destructive" className="my-4">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              There was a problem removing your interest. Please try again.
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter className="flex flex-col sm:flex-row justify-center gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isPending}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleRemoveInterest}
            disabled={isPending}
            variant="destructive"
            className="w-full sm:w-auto"
          >
            {isPending
              ? "Removing..."
              : previousDecision === true
                ? "Withdraw Application"
                : "Remove Decision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
